import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { mapPublicOrder } from './orderTracking.mapper';
import { MotionConfig, motion, PanInfo } from 'framer-motion';
import useIsDesktop from './useIsDesktop';
import { TrackingService } from '../../../services/tracking.service';
import {
	TrackingOrder,
	TrackingTab,
	TrackingViewStatus,
} from './orderTracking.interface';
import {
	TRACKING_DRAWER_TRANSITION,
	TRACKING_POLL_INTERVAL_MS,
	TRACKING_SHEET_COLLAPSED_VH,
	TRACKING_SHEET_EXPANDED_VH,
} from './orderTracking.constants';
import TrackingMap from './components/TrackingMap';
import EtaCard from './components/EtaCard';
import RouteLegend from './components/RouteLegend';
import TrackingHeader from './components/TrackingHeader';
import TruckList from './components/TruckList';
import TruckSwitcher from './components/TruckSwitcher';
import TrackingState from './components/TrackingState';
import TrackingTabs from './components/TrackingTabs';
import TrackingDocuments from './components/TrackingDocuments';
import {
	driverNotice,
	holdLastSeenPositions,
	isTruckAssigned,
	totalDocuments,
} from './orderTracking.utils';
import PublicHeader from '../../../components/PublicHeader/PublicHeader';
import './order-tracking.css';

const OrderTracking: React.FC = () => {
	const { token } = useParams<{ token: string }>();
	const { t } = useTranslate();
	const [status, setStatus] = useState<TrackingViewStatus>(
		token ? 'loading' : 'invalid',
	);
	const [order, setOrder] = useState<TrackingOrder>();
	const [selectedTruckId, setSelectedTruckId] = useState<number>();
	const [tab, setTab] = useState<TrackingTab>('general');
	const [sheetExpanded, setSheetExpanded] = useState<boolean>(false);
	const isDesktop = useIsDesktop();
	const [highlight, setHighlight] = useState<{
		truckId: number;
		token: number;
	}>();

	const load = useCallback(
		async (silent = false) => {
			if (!token) {
				setStatus('invalid');
				return;
			}
			if (!silent) setStatus('loading');
			try {
				const { data } = await TrackingService.getPublicOrder(token);
				const next = mapPublicOrder(data, { routeMode: 'toDelivery' });
				setOrder((previous) => holdLastSeenPositions(previous, next));
				setStatus('ready');
			} catch (e) {
				if (!silent) setStatus('error');
			}
		},
		[token],
	);

	useEffect(() => {
		load().then(() => {});
	}, [load]);

	useEffect(() => {
		if (status !== 'ready') return;
		const timer = setInterval(() => {
			load(true).then(() => {});
		}, TRACKING_POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [status, load]);

	useEffect(() => {
		if (order?.deliveryMode === 'single' && order.trucks.length === 1) {
			setSelectedTruckId(order.trucks[0].id);
		}
	}, [order?.deliveryMode, order?.trucks]);

	const toggleTruck = (truckId: number) => {
		// A single-truck order keeps its truck selected; there is nothing to switch.
		if (order?.deliveryMode === 'single' && order.trucks.length === 1) return;
		const truck = order?.trucks.find((item) => item.id === truckId);
		if (truck && !isTruckAssigned(truck)) return;
		setSelectedTruckId((current) =>
			current === truckId ? undefined : truckId,
		);
	};

	if (status !== 'ready' || !order) {
		return (
			<TrackingState
				status={status === 'ready' ? 'error' : status}
				canRetry={status === 'error' && !!token}
				onRetry={() => load()}
			/>
		);
	}

	const selectedTruck = order.trucks.find(
		(truck) => truck.id === selectedTruckId,
	);
	const multipleTrucks = order.trucks.length > 1;

	const notice = selectedTruck
		? driverNotice(order, selectedTruck, 'toDelivery')
		: undefined;
	const cardNotice = notice && !notice.onMarker ? notice.key : undefined;

	const sheetHeightVh = sheetExpanded
		? TRACKING_SHEET_EXPANDED_VH
		: TRACKING_SHEET_COLLAPSED_VH;
	const mapBottomInset = isDesktop
		? 0
		: Math.round((window.innerHeight * TRACKING_SHEET_COLLAPSED_VH) / 100);

	const onSheetDragEnd = (_: unknown, info: PanInfo) => {
		if (info.offset.y < -40 || info.velocity.y < -300) setSheetExpanded(true);
		if (info.offset.y > 40 || info.velocity.y > 300) setSheetExpanded(false);
	};

	const panelContent = (
		<>
			<TrackingTabs
				value={tab}
				documentsCount={totalDocuments(order.trucks)}
				onChange={setTab}
			/>
			{tab === 'general' ? (
				<>
					<TrackingHeader order={order} />
					<TruckList
						trucks={order.trucks}
						selectedTruckId={selectedTruckId}
						onToggle={toggleTruck}
						onClearSelection={() => setSelectedTruckId(undefined)}
						collapsible={order.deliveryMode === 'multiple'}
						onOpenDocuments={(truckId) => {
							setTab('documents');
							setHighlight({ truckId, token: Date.now() });
						}}
					/>
				</>
			) : (
				<TrackingDocuments
					trucks={order.trucks}
					highlight={highlight}
					onHighlightEnd={() => setHighlight(undefined)}
				/>
			)}
		</>
	);

	return (
		<MotionConfig reducedMotion="user">
			<div className="relative flex h-screen h-[100dvh] w-full overflow-hidden bg-alabaster">
				<PublicHeader />
				<main className="absolute inset-0 lg:relative lg:inset-auto lg:flex-1 min-w-0">
					<TrackingMap
						order={order}
						selectedTruckId={selectedTruckId}
						onSelectTruck={toggleTruck}
						bottomInset={mapBottomInset}
					/>
					{selectedTruck ? (
						<EtaCard
							truck={selectedTruck}
							distanceKm={order.distanceKm}
							updatedAt={order.updatedAt}
							notice={cardNotice}
						/>
					) : null}
					<div
						className="absolute left-4 sm:left-5 z-[600] flex flex-col items-stretch gap-3"
						style={{ bottom: isDesktop ? 24 : mapBottomInset + 16 }}
					>
						<TruckSwitcher
							trucks={order.trucks}
							selectedTruckId={selectedTruckId}
							onSelect={setSelectedTruckId}
						/>
						<RouteLegend multipleTrucks={multipleTrucks} />
					</div>
				</main>

				{isDesktop ? (
					<aside className="w-[35%] min-w-[380px] flex-none min-h-0 pt-20 bg-white border-0 border-l border-solid border-track-line overflow-y-auto overflow-x-hidden">
						{panelContent}
					</aside>
				) : (
					<motion.aside
						className="absolute left-0 right-0 bottom-0 z-[700] flex flex-col bg-white rounded-t-2xl border border-solid border-track-line shadow-[0_-8px_32px_rgba(31,31,39,0.12)] overflow-hidden"
						initial={false}
						animate={{ height: `${sheetHeightVh}vh` }}
						transition={TRACKING_DRAWER_TRANSITION}
					>
						<motion.button
							type="button"
							aria-label={t(
								'orderTracking.toggleDetails',
								'Show or hide details',
							)}
							drag="y"
							dragConstraints={{ top: 0, bottom: 0 }}
							dragElastic={0.15}
							onDragEnd={onSheetDragEnd}
							onClick={() => setSheetExpanded((value) => !value)}
							className="flex-none w-full h-7 flex items-center justify-center border-none bg-transparent cursor-grab active:cursor-grabbing touch-none"
						>
							<span className="w-10 h-[5px] rounded-full bg-track-line-strong" />
						</motion.button>
						<div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
							{panelContent}
						</div>
					</motion.aside>
				)}
			</div>
		</MotionConfig>
	);
};

export default OrderTracking;
